import { useCallback, useLayoutEffect } from 'react'
import { useConnect, useConnectors, useDisconnect, type Connector } from 'wagmi'
import { ConnectorAlreadyConnectedError } from 'wagmi'
import { useGlobalState } from '@evm-ui/hooks/useGlobalState'
import type { Provider } from '@evm-ui/utils/ethers'
import { IS_CYPRESS } from '@ui/lib/env'
import { useCurve } from './CurveContext'
import type { Wallet } from './types'

const state: { provider: Provider | null; wallet: Wallet | null } = { provider: null, wallet: null }

export const useWallet = () => {
  // modal state needs to be global because every call creates new state
  const [showModal, setShowModal] = useGlobalState<boolean>('showConnectModal', false)
  const closeModal = useCallback(() => setShowModal(false), [setShowModal])
  const { wallet, provider, connectState } = useCurve()
  const connectors = useConnectors()
  useLayoutEffect(() => {
    state.wallet = wallet ?? null
    state.provider = provider ?? null
  }, [wallet, provider])

  // use the async functions so we can properly handle the promise failures. We could instead use query state in the future.
  const { mutateAsync: connectAsync } = useConnect()
  const { mutateAsync: disconnectAsync, connectors: connectedConnectors } = useDisconnect()

  /**
   * Disconnects every connected wallet, otherwise wagmi switches to the next connected one.
   * Other wallets are left alone, as some open a popup when asked to disconnect.
   */
  const disconnect = useCallback(
    async () => await Promise.all(connectedConnectors.map(connector => disconnectAsync({ connector }))),
    [connectedConnectors, disconnectAsync],
  )

  // Opens modal when no connector given (clicking 'Connect Wallet' button), otherwise connects directly with the provided connector
  const connect = useCallback(
    async (connector?: Connector) => {
      // When using Cypress, we want to use the one and only (test) connector without blocking modal
      if (!connector && !IS_CYPRESS) {
        setShowModal(true)
        return
      }

      try {
        await connectAsync({ connector: connector ?? connectors[0] })
        setShowModal(false)
      } catch (err) {
        if (err instanceof ConnectorAlreadyConnectedError) return setShowModal(false)
        console.error('Error connecting wallet:', err)
        throw err
      }
    },
    [connectAsync, connectors, setShowModal],
  )

  return { wallet, connectors, connect, disconnect, provider, showModal, closeModal, connectState }
}

useWallet.getState = () => ({ wallet: state.wallet, provider: state.provider })
